import { useEffect, useRef, useState } from 'react';

// Detected once — a demo never switches mode mid-session.
const REDUCED_MOTION =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Drives a Knowledge-page demo through its step script.
 *
 * `steps` is an array of scenes: { caption, ms, ...anything the demo renders
 * from }. The hook advances an index on a timer (each step shows for its `ms`,
 * default 2000), loops back to the start after `loopPauseMs`, and pauses while
 * the user hovers the stage (DemoStage wires `setPaused`).
 *
 * Under prefers-reduced-motion there are no timers at all: the demo parks on
 * its final scene and DemoStage renders the captions as a numbered list.
 */
export default function useDemoScript(steps, { loopPauseMs = 1800 } = {}) {
  const [index, setIndex]   = useState(REDUCED_MOTION ? steps.length - 1 : 0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (REDUCED_MOTION || paused) return undefined;
    const last = index === steps.length - 1;
    const wait = (steps[index]?.ms ?? 2000) + (last ? loopPauseMs : 0);
    timerRef.current = setTimeout(() => {
      setIndex(last ? 0 : index + 1);
    }, wait);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, steps, loopPauseMs]);

  return {
    step: steps[index],
    index,
    total: steps.length,
    paused,
    setPaused,
    reduced: REDUCED_MOTION,
  };
}
